/**
 * MindMap Zustand Store
 *
 * Central state management for the Second Brain application.
 * Handles maps, nodes, edges, UI state, and orchestrates the AI workflow.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import * as supabaseService from '../services/supabaseService';
import * as geminiService from '../services/geminiService';
import * as pdfService from '../services/pdfService';
import * as excelService from '../services/excelService';

// Cluster colors for visual grouping
const CLUSTER_COLORS = [
  { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6', text: '#60a5fa' },   // Blue
  { bg: 'rgba(16, 185, 129, 0.15)', border: '#10b981', text: '#34d399' },   // Green
  { bg: 'rgba(245, 158, 11, 0.15)', border: '#f59e0b', text: '#fbbf24' },   // Amber
  { bg: 'rgba(239, 68, 68, 0.15)', border: '#ef4444', text: '#f87171' },    // Red
  { bg: 'rgba(168, 85, 247, 0.15)', border: '#a855f7', text: '#c084fc' },   // Purple
  { bg: 'rgba(236, 72, 153, 0.15)', border: '#ec4899', text: '#f472b6' },   // Pink
  { bg: 'rgba(20, 184, 166, 0.15)', border: '#14b8a6', text: '#2dd4bf' },   // Teal
  { bg: 'rgba(249, 115, 22, 0.15)', border: '#f97316', text: '#fb923c' },   // Orange
];

// Get consistent color for a cluster tag
const getClusterColor = (tag, existingAssignments = {}) => {
  if (existingAssignments[tag]) {
    return existingAssignments[tag];
  }
  const usedIndices = Object.values(existingAssignments).map(c =>
    CLUSTER_COLORS.findIndex(cc => cc.border === c.border)
  );
  const availableIndex = CLUSTER_COLORS.findIndex((_, i) => !usedIndices.includes(i));
  return CLUSTER_COLORS[availableIndex !== -1 ? availableIndex : Object.keys(existingAssignments).length % CLUSTER_COLORS.length];
};

export const useMapStore = create(
  subscribeWithSelector((set, get) => ({
    // =====================================================
    // STATE
    // =====================================================

    // User & Auth
    user: null,
    profile: null,
    isAuthenticated: false,

    // Maps
    currentMap: null,
    maps: [],
    isLoadingMaps: false,

    // Nodes & Edges (React Flow format)
    nodes: [],
    edges: [],
    isLoadingNodes: false,

    // Cluster tracking
    clusterColors: {},

    // UI State
    selectedNode: null,
    isAddingNode: false,
    addNodeType: null,
    addNodePosition: null,

    // Chat Sidebar
    isChatOpen: false,
    chatSessions: [],
    currentChatSession: null,
    chatMessages: [],
    isChatLoading: false,
    suggestedNode: null, // AI-suggested node from chat

    // Time Travel
    timeRange: { start: null, end: null },
    currentTime: null,
    isTimeTraveling: false,

    // AI Processing
    isProcessingAI: false,
    aiStatus: '',

    // Ghost Nodes
    ghostNodes: [],
    isAnalyzingGaps: false,

    // Viewport
    viewport: { x: 0, y: 0, zoom: 1 },

    // Settings
    settings: {
      autoConnect: true,
      devilsAdvocate: true,
      semanticZoom: true,
      showGhostNodes: true,
    },

    // Attachment Modal
    attachmentModalNodeId: null,
    isUploadingAttachment: false,

    // Edit Modal
    editModalNodeId: null,

    // Settings Modal
    isSettingsOpen: false,

    // =====================================================
    // AUTH ACTIONS
    // =====================================================

    setUser: (user) => set({ user, isAuthenticated: !!user }),

    setProfile: (profile) => set({ profile }),

    initAuth: async () => {
      try {
        // Get initial session
        const session = await supabaseService.auth.getSession();
        if (session?.user) {
          set({ user: session.user, isAuthenticated: true });
          const profile = await supabaseService.profiles.get(session.user.id);
          set({ profile });
        }

        // Listen for auth state changes (important for OAuth callbacks)
        supabaseService.auth.onAuthStateChange(async (event, session) => {
          console.log('Auth state change:', event, session?.user?.email);

          if (event === 'SIGNED_IN' && session?.user) {
            const { profile: existingProfile } = get();
            set({ user: session.user, isAuthenticated: true });
            // Only fetch profile if not already loaded
            if (!existingProfile || existingProfile.id !== session.user.id) {
              try {
                const profile = await supabaseService.profiles.get(session.user.id);
                set({ profile });
              } catch (err) {
                console.error('Error fetching profile:', err);
              }
            }
          } else if (event === 'SIGNED_OUT') {
            set({
              user: null,
              profile: null,
              isAuthenticated: false,
              currentMap: null,
              nodes: [],
              edges: [],
            });
          }
        });

        return !!session?.user;
      } catch (error) {
        console.error('Auth init error:', error);
        return false;
      }
    },

    signOut: async () => {
      try {
        await supabaseService.auth.signOut();
      } catch (error) {
        console.warn('SignOut error:', error);
      }
      // Always clear local state
      set({
        user: null,
        profile: null,
        isAuthenticated: false,
        currentMap: null,
        nodes: [],
        edges: [],
      });
    },

    // =====================================================
    // MAP ACTIONS
    // =====================================================

    loadMaps: async () => {
      set({ isLoadingMaps: true });
      try {
        const maps = await supabaseService.maps.getAll();
        set({ maps, isLoadingMaps: false });
      } catch (error) {
        console.error('Error loading maps:', error);
        set({ isLoadingMaps: false });
      }
    },

    loadMap: async (mapId) => {
      set({ isLoadingNodes: true, nodes: [], edges: [] });
      try {
        const map = await supabaseService.maps.get(mapId);
        const [nodesData, edgesData, timeRange] = await Promise.all([
          supabaseService.nodes.getAllWithGhosts(mapId),
          supabaseService.edges.getAll(mapId),
          supabaseService.timeTravel.getTimeRange(mapId),
        ]);

        // Build cluster color assignments
        const clusterColors = {};
        nodesData.forEach(node => {
          if (node.cluster_tag && !clusterColors[node.cluster_tag]) {
            clusterColors[node.cluster_tag] = getClusterColor(node.cluster_tag, clusterColors);
          }
        });

        // Transform to React Flow format
        const nodes = nodesData.map(node => ({
          id: node.id,
          type: node.type === 'ghost' ? 'ghost' : node.type === 'challenge' ? 'challenge' : 'custom',
          position: node.position,
          data: {
            ...node,
            clusterColor: clusterColors[node.cluster_tag],
          },
        }));

        const edges = edgesData.map(edge => ({
          id: edge.id,
          source: edge.source_id,
          target: edge.target_id,
          label: edge.label,
          type: edge.type === 'challenge' ? 'challenge' : 'default',
          animated: edge.style?.animated,
          data: edge,
        }));

        set({
          currentMap: map,
          nodes,
          edges,
          clusterColors,
          timeRange,
          currentTime: timeRange.end,
          isLoadingNodes: false,
          viewport: map.viewport || { x: 0, y: 0, zoom: 1 },
        });
      } catch (error) {
        console.error('Error loading map:', error);
        set({ isLoadingNodes: false });
      }
    },

    createMap: async (title, isMain = false) => {
      const { user } = get();
      if (!user) return null;

      try {
        const map = await supabaseService.maps.create({
          user_id: user.id,
          title,
          is_main: isMain,
        });
        set(state => ({ maps: [map, ...state.maps] }));
        return map;
      } catch (error) {
        console.error('Error creating map:', error);
        return null;
      }
    },

    updateMap: async (updates) => {
      const { currentMap } = get();
      if (!currentMap) return;

      try {
        const updated = await supabaseService.maps.update(currentMap.id, updates);
        set({ currentMap: updated });
      } catch (error) {
        console.error('Error updating map:', error);
      }
    },

    deleteMap: async (mapId) => {
      try {
        await supabaseService.maps.delete(mapId);
        set(state => ({
          maps: state.maps.filter(m => m.id !== mapId),
          currentMap: state.currentMap?.id === mapId ? null : state.currentMap,
        }));
      } catch (error) {
        console.error('Error deleting map:', error);
      }
    },

    // =====================================================
    // NODE ACTIONS (The Core AI Workflow)
    // =====================================================

    /**
     * Add a new node - This triggers the entire AI workflow:
     * 1. Process content (scrape URL, describe image, or use text)
     * 2. Generate embedding
     * 3. Find similar nodes via vector search
     * 4. Analyze connections with AI
     * 5. Create edges
     * 6. Optionally trigger Devil's Advocate
     */
    addNode: async (type, content, position, metadata = {}) => {
      const { currentMap, user, settings, clusterColors } = get();
      if (!currentMap || !user) return null;

      set({ isProcessingAI: true, aiStatus: 'Processing content...' });

      try {
        let processedContent = content;
        let title = metadata.title;
        let clusterTag = null;
        let imageUrl = metadata.imageUrl;
        let imageDescription = null;
        let sourceUrl = metadata.sourceUrl;
        let scrapedContent = null;

        // Step 1: Process based on type
        if (type === 'url' && sourceUrl) {
          set({ aiStatus: 'Fetching and analyzing URL...' });
          const urlData = await geminiService.scrapeAndSummarize(sourceUrl);
          processedContent = urlData.contentForEmbedding || urlData.summary;
          title = urlData.title;
          clusterTag = urlData.suggestedCluster;
          scrapedContent = urlData.summary;
        } else if (type === 'image' && metadata.imageBase64) {
          set({ aiStatus: 'Analyzing image with AI Vision...' });
          const imageData = await geminiService.describeImage(
            metadata.imageBase64,
            metadata.mimeType
          );
          processedContent = imageData.description;
          title = imageData.title;
          imageDescription = imageData.description;
          clusterTag = imageData.suggestedTags?.[0];
        }

        // Step 2: Generate embedding (include additional context for better search)
        set({ aiStatus: 'Generating semantic embedding...' });
        const contentForEmbedding = metadata.additionalContext
          ? `${processedContent}\n\nContext: ${metadata.additionalContext}`
          : processedContent;
        const embedding = await geminiService.generateEmbedding(contentForEmbedding);

        // Step 3: Get cluster tag if not already set
        if (!clusterTag) {
          const existingClusters = Object.keys(clusterColors);
          clusterTag = await geminiService.generateClusterTag(processedContent, existingClusters);
        }

        // Assign cluster color
        const newClusterColors = { ...clusterColors };
        if (!newClusterColors[clusterTag]) {
          newClusterColors[clusterTag] = getClusterColor(clusterTag, newClusterColors);
        }

        // Step 4: Save node to database
        set({ aiStatus: 'Saving to knowledge base...' });
        const nodeData = {
          map_id: currentMap.id,
          user_id: user.id,
          content: processedContent,
          type,
          title: title || processedContent.substring(0, 50),
          cluster_tag: clusterTag,
          cluster_color: newClusterColors[clusterTag]?.border,
          position,
          source_url: sourceUrl,
          scraped_content: scrapedContent,
          image_url: imageUrl,
          image_description: imageDescription,
          additional_context: metadata.additionalContext || null,
          is_approved: true,
        };

        const newNode = await supabaseService.nodes.createWithEmbedding(nodeData, embedding);

        // Add to local state
        const reactFlowNode = {
          id: newNode.id,
          type: 'custom',
          position: newNode.position,
          data: {
            ...newNode,
            clusterColor: newClusterColors[clusterTag],
          },
        };

        set(state => ({
          nodes: [...state.nodes, reactFlowNode],
          clusterColors: newClusterColors,
        }));

        // Step 5: Find similar nodes
        set({ aiStatus: 'Finding semantic connections...' });
        const similarNodes = await supabaseService.vectorSearch.matchNodes(
          embedding,
          currentMap.id,
          { threshold: 0.6, limit: 5, excludeNodeId: newNode.id }
        );

        let connections = [];

        if (similarNodes.length > 0) {
          // Step 6: Analyze connections with AI
          set({ aiStatus: 'AI reasoning about connections...' });
          const analysis = await geminiService.analyzeConnections(newNode, similarNodes);
          connections = analysis.connections || [];

          // Create edges
          if (connections.length > 0) {
            const edgesData = connections.map(conn => ({
              map_id: currentMap.id,
              source_id: newNode.id,
              target_id: conn.targetId,
              label: conn.label,
              ai_reasoning: conn.reasoning,
              strength: conn.strength || 0.7,
              type: 'semantic',
              is_ai_generated: true,
            }));

            const createdEdges = await supabaseService.edges.createMany(edgesData);

            // Add to local state
            const reactFlowEdges = createdEdges.map(edge => ({
              id: edge.id,
              source: edge.source_id,
              target: edge.target_id,
              label: edge.label,
              animated: true,
              data: edge,
            }));

            set(state => ({ edges: [...state.edges, ...reactFlowEdges] }));
          }
        }

        // Step 7: Devil's Advocate (occasional challenges)
        if (settings.devilsAdvocate && Math.random() < 0.3) { // 30% chance
          set({ aiStatus: "Devil's Advocate analyzing..." });
          const challenge = await geminiService.generateChallenge(newNode, similarNodes);

          if (challenge) {
            await get().createChallengeNode(newNode.id, challenge, position);
          }
        }

        set({ isProcessingAI: false, aiStatus: '' });
        return newNode;
      } catch (error) {
        console.error('Error adding node:', error);
        set({ isProcessingAI: false, aiStatus: '' });
        throw error;
      }
    },

    /**
     * Create a challenge node (Devil's Advocate)
     */
    createChallengeNode: async (targetNodeId, challenge, nearPosition) => {
      const { currentMap, user } = get();
      if (!currentMap || !user) return;

      const challengePosition = {
        x: nearPosition.x + 250,
        y: nearPosition.y - 50,
      };

      const embedding = await geminiService.generateEmbedding(challenge.content);

      const nodeData = {
        map_id: currentMap.id,
        user_id: user.id,
        content: challenge.content,
        type: 'challenge',
        title: challenge.title,
        cluster_tag: 'challenge',
        position: challengePosition,
        challenges_node_id: targetNodeId,
        metadata: { challengeType: challenge.challengeType },
        is_approved: true,
      };

      const newNode = await supabaseService.nodes.createWithEmbedding(nodeData, embedding);

      // Create edge to challenged node
      const edge = await supabaseService.edges.create({
        map_id: currentMap.id,
        source_id: newNode.id,
        target_id: targetNodeId,
        label: 'challenges',
        type: 'challenge',
        ai_reasoning: challenge.reasoning,
        is_ai_generated: true,
      });

      // Add to local state
      set(state => ({
        nodes: [...state.nodes, {
          id: newNode.id,
          type: 'challenge',
          position: newNode.position,
          data: newNode,
        }],
        edges: [...state.edges, {
          id: edge.id,
          source: edge.source_id,
          target: edge.target_id,
          label: edge.label,
          type: 'challenge',
          data: edge,
        }],
      }));
    },

    updateNode: async (nodeId, updates) => {
      try {
        const updated = await supabaseService.nodes.update(nodeId, updates);
        set(state => ({
          nodes: state.nodes.map(n =>
            n.id === nodeId ? { ...n, data: { ...n.data, ...updated } } : n
          ),
        }));
      } catch (error) {
        console.error('Error updating node:', error);
      }
    },

    updateNodePosition: async (nodeId, position) => {
      try {
        await supabaseService.nodes.updatePosition(nodeId, position);
        set(state => ({
          nodes: state.nodes.map(n =>
            n.id === nodeId ? { ...n, position } : n
          ),
        }));
      } catch (error) {
        console.error('Error updating node position:', error);
      }
    },

    deleteNode: async (nodeId) => {
      try {
        await supabaseService.edges.deleteForNode(nodeId);
        await supabaseService.nodes.delete(nodeId);
        set(state => ({
          nodes: state.nodes.filter(n => n.id !== nodeId),
          edges: state.edges.filter(e => e.source !== nodeId && e.target !== nodeId),
          selectedNode: state.selectedNode?.id === nodeId ? null : state.selectedNode,
        }));
      } catch (error) {
        console.error('Error deleting node:', error);
      }
    },

    // =====================================================
    // GAP ANALYSIS (Ghost Nodes)
    // =====================================================

    analyzeGaps: async () => {
      const { currentMap, nodes, edges } = get();
      if (!currentMap) return;

      set({ isAnalyzingGaps: true });
      try {
        const nodeData = nodes.map(n => n.data);
        const result = await geminiService.analyzeGaps(nodeData, edges);

        set({ ghostNodes: result.ghostNodes, isAnalyzingGaps: false });
        return result;
      } catch (error) {
        console.error('Error analyzing gaps:', error);
        set({ isAnalyzingGaps: false });
        return { ghostNodes: [], assessment: null };
      }
    },

    approveGhostNode: async (ghostNode) => {
      const { currentMap, user, clusterColors } = get();
      if (!currentMap || !user) return;

      // Create the ghost node as a real node
      const position = { x: Math.random() * 500, y: Math.random() * 500 };
      await get().addNode('text', ghostNode.content, position, {
        title: ghostNode.title,
      });

      // Remove from ghost nodes list
      set(state => ({
        ghostNodes: state.ghostNodes.filter(g => g.title !== ghostNode.title),
      }));
    },

    dismissGhostNode: (ghostNode) => {
      set(state => ({
        ghostNodes: state.ghostNodes.filter(g => g.title !== ghostNode.title),
      }));
    },

    // =====================================================
    // CHAT (RAG Interface with Sessions)
    // =====================================================

    toggleChat: () => set(state => ({ isChatOpen: !state.isChatOpen })),

    // Load all sessions for current map
    loadChatSessions: async () => {
      const { currentMap } = get();
      if (!currentMap) return;

      try {
        const sessions = await supabaseService.chat.getSessions(currentMap.id);
        set({ chatSessions: sessions });

        // Auto-select most recent session or create new one
        if (sessions.length > 0) {
          await get().selectChatSession(sessions[0].id);
        }
      } catch (error) {
        console.error('Error loading chat sessions:', error);
      }
    },

    // Select a chat session and load its messages
    selectChatSession: async (sessionId) => {
      try {
        const messages = await supabaseService.chat.getMessages(sessionId);
        const session = get().chatSessions.find(s => s.id === sessionId);
        set({ currentChatSession: session, chatMessages: messages });
      } catch (error) {
        console.error('Error loading session messages:', error);
      }
    },

    // Create a new chat session
    createChatSession: async (title = 'New Chat') => {
      const { currentMap, user } = get();
      if (!currentMap || !user) return null;

      try {
        const session = await supabaseService.chat.createSession(currentMap.id, user.id, title);
        set(state => ({
          chatSessions: [session, ...state.chatSessions],
          currentChatSession: session,
          chatMessages: [],
        }));
        return session;
      } catch (error) {
        console.error('Error creating chat session:', error);
        return null;
      }
    },

    // Delete a chat session
    deleteChatSession: async (sessionId) => {
      try {
        await supabaseService.chat.deleteSession(sessionId);
        const { chatSessions, currentChatSession } = get();
        const updatedSessions = chatSessions.filter(s => s.id !== sessionId);

        set({ chatSessions: updatedSessions });

        // If deleted current session, select another or clear
        if (currentChatSession?.id === sessionId) {
          if (updatedSessions.length > 0) {
            await get().selectChatSession(updatedSessions[0].id);
          } else {
            set({ currentChatSession: null, chatMessages: [] });
          }
        }
      } catch (error) {
        console.error('Error deleting chat session:', error);
      }
    },

    // Legacy: load chat history (for backwards compat)
    loadChatHistory: async () => {
      await get().loadChatSessions();
    },

    sendChatMessage: async (query) => {
      const { currentMap, user, chatMessages, currentChatSession } = get();
      if (!currentMap || !user) return;

      set({ isChatLoading: true });

      try {
        // Auto-create session if none exists
        let sessionId = currentChatSession?.id;
        if (!sessionId) {
          const newSession = await get().createChatSession();
          sessionId = newSession?.id;
        }

        // Add user message
        const userMessage = await supabaseService.chat.addMessage({
          map_id: currentMap.id,
          user_id: user.id,
          session_id: sessionId,
          role: 'user',
          content: query,
        });

        set(state => ({ chatMessages: [...state.chatMessages, userMessage] }));

        // Generate embedding for the query
        const queryEmbedding = await geminiService.generateEmbedding(query);

        // PRIORITY SEARCH: First search current map, then supplement with global results
        // This ensures relevant results from the current context come first
        // Lower threshold (0.35) to catch cross-language semantic matches
        const [currentMapNodes, currentMapAttachments] = await Promise.all([
          supabaseService.vectorSearch.matchNodes(queryEmbedding, currentMap.id, { threshold: 0.35, limit: 10 }),
          supabaseService.vectorSearch.matchAttachmentsInMap(queryEmbedding, currentMap.id, { threshold: 0.35, limit: 8 }),
        ]);

        // Format current map results with higher priority
        const currentMapFormatted = (currentMapNodes || []).map(node => ({
          id: node.id,
          map_id: currentMap.id,
          map_title: currentMap.title,
          content: node.content,
          title: node.title,
          type: node.type,
          similarity: node.similarity + 0.1, // Boost current map results
          created_at: node.created_at,
          isCurrentMap: true,
        }));

        const currentAttachmentsFormatted = (currentMapAttachments || []).map(att => ({
          id: att.node_id,
          map_id: currentMap.id,
          map_title: currentMap.title,
          content: `[Attachment: ${att.file_name}] ${att.extracted_text || ''} ${att.summary || ''}`,
          type: 'attachment',
          similarity: att.similarity + 0.1, // Boost current map results
          file_url: att.file_url,
          isCurrentMap: true,
        }));

        // NOTE: Global search disabled by default to prevent irrelevant cross-map results
        // Chat now searches ONLY in the current map for focused, relevant answers
        // Global search can be re-enabled in the future with explicit user request

        // Combine and sort by similarity - only current map results
        const context = [
          ...currentMapFormatted,
          ...currentAttachmentsFormatted,
        ].sort((a, b) => b.similarity - a.similarity).slice(0, 15);

        // Generate response using RAG - send ALL messages from current session
        const response = await geminiService.chatWithBrain(query, context, chatMessages);

        // Save assistant message
        const assistantMessage = await supabaseService.chat.addMessage({
          map_id: currentMap.id,
          user_id: user.id,
          session_id: sessionId,
          role: 'assistant',
          content: response.content,
          context_node_ids: response.sources.map(s => s.id),
        });

        set(state => ({
          chatMessages: [...state.chatMessages, assistantMessage],
          isChatLoading: false,
          suggestedNode: response.suggestedNode || null,
        }));

        // Auto-generate session title from first message
        if (chatMessages.length === 0 && currentChatSession?.title === 'New Chat') {
          const title = query.slice(0, 50) + (query.length > 50 ? '...' : '');
          await supabaseService.chat.updateSession(sessionId, { title });
          set(state => ({
            currentChatSession: { ...state.currentChatSession, title },
            chatSessions: state.chatSessions.map(s =>
              s.id === sessionId ? { ...s, title } : s
            ),
          }));
        }

        return response;
      } catch (error) {
        console.error('Error sending chat message:', error);
        set({ isChatLoading: false });
        throw error;
      }
    },

    // Accept suggested node from chat
    acceptSuggestedNode: async () => {
      const { suggestedNode, viewport } = get();
      if (!suggestedNode) return;

      // Calculate position near center of viewport
      const position = {
        x: -viewport.x + window.innerWidth / 2 / viewport.zoom - 100,
        y: -viewport.y + window.innerHeight / 2 / viewport.zoom - 50,
      };

      // Add node using existing function
      await get().addNode('text', suggestedNode.content, position, {
        title: suggestedNode.title,
      });

      // Clear suggestion
      set({ suggestedNode: null });
    },

    // Dismiss suggested node
    dismissSuggestedNode: () => set({ suggestedNode: null }),

    // =====================================================
    // TIME TRAVEL
    // =====================================================

    setCurrentTime: async (timestamp) => {
      const { currentMap, isTimeTraveling } = get();
      if (!currentMap) return;

      if (!isTimeTraveling) {
        set({ isTimeTraveling: true });
      }

      try {
        const [nodesAtTime, edgesAtTime] = await Promise.all([
          supabaseService.timeTravel.getNodesAtTime(currentMap.id, timestamp),
          supabaseService.timeTravel.getEdgesAtTime(currentMap.id, timestamp),
        ]);

        const nodes = nodesAtTime.map(node => ({
          id: node.id,
          type: node.type === 'ghost' ? 'ghost' : 'custom',
          position: node.position,
          data: node,
        }));

        const edges = edgesAtTime.map(edge => ({
          id: edge.id,
          source: edge.source_id,
          target: edge.target_id,
          label: edge.label,
          data: edge,
        }));

        set({ nodes, edges, currentTime: new Date(timestamp) });
      } catch (error) {
        console.error('Error time traveling:', error);
      }
    },

    exitTimeTravel: async () => {
      const { currentMap } = get();
      if (!currentMap) return;

      set({ isTimeTraveling: false });
      await get().loadMap(currentMap.id);
    },

    // =====================================================
    // REACT FLOW HANDLERS
    // =====================================================

    onNodesChange: (changes) => {
      set(state => {
        const newNodes = [...state.nodes];
        changes.forEach(change => {
          if (change.type === 'position' && change.position) {
            const nodeIndex = newNodes.findIndex(n => n.id === change.id);
            if (nodeIndex !== -1) {
              newNodes[nodeIndex] = {
                ...newNodes[nodeIndex],
                position: change.position,
              };
            }
          }
        });
        return { nodes: newNodes };
      });
    },

    onEdgesChange: (changes) => {
      // Handle edge changes if needed
    },

    onConnect: async (connection) => {
      const { currentMap } = get();
      if (!currentMap) return;

      try {
        const edge = await supabaseService.edges.create({
          map_id: currentMap.id,
          source_id: connection.source,
          target_id: connection.target,
          type: 'manual',
          is_ai_generated: false,
        });

        set(state => ({
          edges: [...state.edges, {
            id: edge.id,
            source: edge.source_id,
            target: edge.target_id,
            data: edge,
          }],
        }));
      } catch (error) {
        console.error('Error creating edge:', error);
      }
    },

    setViewport: (viewport) => set({ viewport }),

    setSelectedNode: (node) => set({ selectedNode: node }),

    // =====================================================
    // UI STATE
    // =====================================================

    startAddingNode: (type, position) => set({
      isAddingNode: true,
      addNodeType: type,
      addNodePosition: position,
    }),

    cancelAddingNode: () => set({
      isAddingNode: false,
      addNodeType: null,
      addNodePosition: null,
    }),

    updateSettings: (updates) => set(state => ({
      settings: { ...state.settings, ...updates },
    })),

    // =====================================================
    // ATTACHMENT MODAL
    // =====================================================

    openAttachmentModal: (nodeId) => set({ attachmentModalNodeId: nodeId }),
    closeAttachmentModal: () => set({ attachmentModalNodeId: null }),

    addAttachment: async (nodeId, file) => {
      set({ isUploadingAttachment: true, aiStatus: 'Processing attachment...' });
      try {
        let aiData = {};

        // Check file type
        const isImage = file.type.startsWith('image/');
        const isPDF = pdfService.isPDF(file);

        if (isImage) {
          // Convert image to base64 for Gemini Vision
          set({ aiStatus: 'Analyzing image with AI...' });
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              // Extract base64 data (remove data:image/...;base64, prefix)
              const base64Data = reader.result.split(',')[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          // Analyze image with Gemini Vision
          const imageAnalysis = await geminiService.describeImage(base64, file.type);
          aiData.extracted_text = imageAnalysis.description || '';
          aiData.summary = imageAnalysis.concepts?.join(', ') || '';

          // Generate embedding from the extracted text
          if (aiData.extracted_text) {
            set({ aiStatus: 'Generating semantic embedding...' });
            const contentForEmbedding = `${aiData.extracted_text}\n\n${aiData.summary}`;
            aiData.embedding = await geminiService.generateEmbedding(contentForEmbedding);
          }
        } else if (isPDF) {
          // Extract text from PDF
          set({ aiStatus: 'Extracting text from PDF...' });
          const pdfData = await pdfService.extractTextFromPDF(file);

          if (pdfData.text && pdfData.text.trim().length > 0) {
            aiData.extracted_text = pdfData.text;

            // Summarize long PDFs
            if (pdfData.text.length > 2000) {
              set({ aiStatus: 'Summarizing PDF content...' });
              try {
                const summaryResult = await geminiService.summarizePDF(
                  pdfData.text,
                  pdfData.metadata?.title || file.name
                );
                aiData.summary = summaryResult.summary || '';
              } catch (e) {
                console.warn('Could not summarize PDF:', e);
                // Fallback to first 500 chars as summary
                aiData.summary = pdfData.text.substring(0, 500) + '...';
              }
            } else {
              aiData.summary = pdfData.text.substring(0, 500);
            }

            // Generate embedding
            set({ aiStatus: 'Generating semantic embedding...' });
            // Use summary for embedding if available, otherwise use truncated text
            const contentForEmbedding = aiData.summary || pdfData.text.substring(0, 8000);
            aiData.embedding = await geminiService.generateEmbedding(contentForEmbedding);
          } else {
            console.warn('No text extracted from PDF');
            aiData.summary = 'PDF dosyasından metin çıkarılamadı (taranmış veya görsel içerikli olabilir)';
          }
        } else if (excelService.isExcel(file)) {
          // Extract text from Excel
          set({ aiStatus: 'Excel dosyası işleniyor...' });
          const excelData = await excelService.extractTextFromExcel(file);

          if (excelData.text && excelData.text.trim().length > 0) {
            aiData.extracted_text = excelData.text;

            // Summarize long Excel files
            if (excelData.text.length > 2000) {
              set({ aiStatus: 'Excel içeriği özetleniyor...' });
              try {
                const summaryResult = await geminiService.summarizePDF(
                  excelData.text,
                  file.name
                );
                aiData.summary = summaryResult.summary || '';
              } catch (e) {
                console.warn('Could not summarize Excel:', e);
                aiData.summary = `${excelData.sheetCount} sayfa, ${excelData.sheets.map(s => s.name).join(', ')}. ${excelData.text.substring(0, 400)}...`;
              }
            } else {
              aiData.summary = `${excelData.sheetCount} sayfa: ${excelData.sheets.map(s => s.name).join(', ')}. ${excelData.text.substring(0, 400)}`;
            }

            // Generate embedding
            set({ aiStatus: 'Semantic embedding oluşturuluyor...' });
            const contentForEmbedding = aiData.summary || excelData.text.substring(0, 8000);
            aiData.embedding = await geminiService.generateEmbedding(contentForEmbedding);
          } else {
            console.warn('No text extracted from Excel');
            aiData.summary = 'Excel dosyasından veri çıkarılamadı';
          }
        }

        // Upload with AI data
        set({ aiStatus: 'Uploading file...' });
        const attachment = await supabaseService.attachments.upload(nodeId, file, aiData);

        set(state => ({
          nodes: state.nodes.map(n =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, attachments: [...(n.data.attachments || []), attachment] } }
              : n
          ),
          isUploadingAttachment: false,
          aiStatus: null,
        }));
        return attachment;
      } catch (error) {
        console.error('Error uploading attachment:', error);
        set({ isUploadingAttachment: false, aiStatus: null });
        throw error;
      }
    },

    deleteAttachment: async (nodeId, attachmentId) => {
      try {
        await supabaseService.attachments.delete(attachmentId);
        set(state => ({
          nodes: state.nodes.map(n =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, attachments: (n.data.attachments || []).filter(a => a.id !== attachmentId) } }
              : n
          ),
        }));
      } catch (error) {
        console.error('Error deleting attachment:', error);
        throw error;
      }
    },

    // =====================================================
    // EDIT MODAL
    // =====================================================

    openEditModal: (nodeId) => set({ editModalNodeId: nodeId }),
    closeEditModal: () => set({ editModalNodeId: null }),

    // =====================================================
    // SETTINGS MODAL
    // =====================================================

    openSettings: () => set({ isSettingsOpen: true }),
    closeSettings: () => set({ isSettingsOpen: false }),

    // =====================================================
    // EXPORT / DOWNLOAD
    // =====================================================

    exportMap: () => {
      const { currentMap, nodes, edges } = get();
      if (!currentMap) return;

      // Prepare export data
      const exportData = {
        map: {
          title: currentMap.title,
          description: currentMap.description,
          is_main: currentMap.is_main,
          exported_at: new Date().toISOString(),
        },
        nodes: nodes.map(node => ({
          id: node.id,
          title: node.data.title,
          content: node.data.content,
          ai_summary: node.data.ai_summary,
          tags: node.data.tags,
          cluster_tag: node.data.cluster_tag,
          type: node.type,
          position: node.position,
          source_url: node.data.source_url,
        })),
        edges: edges.map(edge => ({
          source: edge.source,
          target: edge.target,
          label: edge.data?.label,
        })),
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentMap.title.replace(/\s+/g, '_')}_export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  }))
);

export default useMapStore;
